import { useEffect, useState } from 'react'
import { TrendPill } from '../components/TrendPill'
import { TRADES, tradeName } from '../data/trades'
import { T, useLocale, useT } from '../i18n'
import { usePageMeta } from '../lib/meta'
import {
  type IndexRow,
  type IndexTrend,
  fmtPrice,
  getIndexLatest,
  getIndexTrend,
} from '../lib/rates'

export function IndexPage() {
  const t = useT()
  const locale = useLocale()
  usePageMeta(t('meta.index.title'), t('meta.index.desc'), '/rate-index')
  const [index, setIndex] = useState<IndexRow[]>([])
  const [trend, setTrend] = useState<Map<number, IndexTrend>>(new Map())
  useEffect(() => {
    getIndexLatest()
      .then(setIndex)
      .catch(() => {})
    getIndexTrend()
      .then(setTrend)
      .catch(() => {})
  }, [])

  return (
    <div className="mx-auto max-w-5xl px-5 pt-12">
      <h1 className="font-display text-4xl font-light text-center">{t('index.h1')}</h1>
      <p className="mt-3 max-w-2xl mx-auto text-center text-muted leading-relaxed">{t('index.intro')}</p>
      <div className="mt-8 overflow-x-auto rounded-lg border border-border-strong">
        <table className="w-full text-sm">
          <thead className="bg-surface-1 text-left text-xs uppercase text-muted">
            <tr>
              <th className="px-4 py-3 font-medium">{t('index.th.lane')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('index.th.median')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('index.th.avg20')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('index.th.range')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('index.th.transit')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('index.th.lanes')}</th>
            </tr>
          </thead>
          <tbody>
            {TRADES.map((tr) => {
              const r = index.find((x) => x.line_category_id === tr.id)
              return (
                <tr key={tr.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <T to={`/trade/${tr.slug}`} className="hover:text-link">
                      {tradeName(tr, locale).name}
                    </T>
                  </td>
                  <td className="px-4 py-3 text-right tnum text-signal font-medium">
                    <span className="inline-flex items-center gap-2">
                      <TrendPill trend={trend.get(tr.id)} />
                      {fmtPrice(r?.median_40hc ?? null)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tnum">{fmtPrice(r?.avg_20gp ?? null)}</td>
                  <td className="px-4 py-3 text-right tnum text-muted">
                    {r ? `${fmtPrice(r.min_40hc)}–${fmtPrice(r.max_40hc)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right tnum">
                    {r?.avg_transit_days ? `${r.avg_transit_days}d` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right tnum text-muted">{r?.lane_count ?? '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-muted">
        {t('index.methodPre')}{' '}
        <T to="/pricing" className="text-link hover:underline">
          {t('index.methodLink')}
        </T>
        ).
      </p>
    </div>
  )
}
